import { Directive, ElementRef, Input, Renderer2, NgZone, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, HostListener } from '@angular/core';

/**
 * Diretiva para animar texto que excede o container.
 * Modos: 'hover' (anima no hover), 'loop' (contínuo), 'hover-loop' (loop no hover)
 * Alvos: 'content' (default), 'self' (elemento desliza), 'firstChild'
 * Funciona em overlays CDK (mat-autocomplete, mat-select, etc.)
 */
@Directive({
  selector: '[appTextSlide]',
})
export class TextSlideOverflowDirective implements AfterViewInit, OnDestroy, OnChanges {
  @Input() mode: 'hover' | 'loop' | 'hover-loop' = 'hover';
  @Input() slideTarget: 'self' | 'content' | 'firstChild' = 'content';
  @Input() speed: number = 30;
  @Input() pauseStart: number = 1.5;
  @Input() pauseEnd: number = 1.5;
  @Input() ellipsis: boolean = false;
  @Input() slideMargin: number = 5;

  private intersectionObserver: IntersectionObserver | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private isVisible: boolean = false;
  private isOverflowing: boolean = false;
  private isHovering: boolean = false;
  private isAnimating: boolean = false;
  private hasInitialized: boolean = false;
  private textElement: HTMLElement | null = null;
  private containerElement: HTMLElement | null = null;
  private styleElement: HTMLStyleElement | null = null;
  private animationTimeoutId: any = null;
  private checkTimeoutId: any = null;
  private readonly UNIQUE_ID = `text-slide-${Math.random().toString(36).substr(2, 9)}`;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.setupIntersectionObserver();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.textElement && (changes['mode'] || changes['speed'] || changes['slideTarget'])) {
      this.scheduleOverflowCheck();
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.isHovering = true;

    if (!this.hasInitialized) this.initializeDirective();
    this.checkOverflow();
    if (!this.isOverflowing) return;

    if (this.mode === 'hover' && !this.isAnimating) {
      this.startHoverAnimation();
    } else if (this.mode === 'hover-loop') {
      this.startLoopAnimation();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.isHovering = false;
    if (this.mode === 'hover-loop') this.stopAnimation();
  }

  private setupIntersectionObserver(): void {
    if (typeof IntersectionObserver === 'undefined') {
      this.initializeDirective();
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const wasVisible = this.isVisible;
          this.isVisible = entry.isIntersecting;

          if (this.isVisible && !wasVisible) {
            if (!this.hasInitialized) {
              this.initializeDirective();
            } else {
              this.scheduleOverflowCheck();
            }
          }
        });
      },
      { root: null, threshold: 0, rootMargin: '50px' }
    );

    this.intersectionObserver.observe(this.el.nativeElement);
  }

  private initializeDirective(): void {
    if (this.hasInitialized) return;
    this.hasInitialized = true;

    requestAnimationFrame(() => {
      this.setupElements();
      this.setupHostStyles();
      this.setupResizeObserver();
      this.setupMutationObserver();
      this.checkOverflow();

      if (this.mode === 'loop' && this.isOverflowing) {
        this.startLoopAnimation();
      }
    });
  }

  private setupElements(): void {
    const host = this.el.nativeElement;

    if (this.slideTarget === 'self') {
      this.textElement = host;
      this.containerElement = host.parentElement;
    } else if (this.slideTarget === 'firstChild') {
      this.containerElement = host;
      this.textElement = host.firstElementChild as HTMLElement;
    } else {
      this.containerElement = host;
      this.textElement = this.getOrCreateContentWrapper();
    }

    if (this.textElement) {
      this.renderer.setAttribute(this.textElement, 'data-text-slide', this.UNIQUE_ID);
    }
  }

  private getOrCreateContentWrapper(): HTMLElement {
    const host = this.el.nativeElement;
    const children = Array.from(host.childNodes);

    const existingWrapper = host.querySelector('[data-slide-wrapper]');
    if (existingWrapper) return existingWrapper as HTMLElement;

    const elementChildren = children.filter(n => n.nodeType === Node.ELEMENT_NODE);
    if (elementChildren.length === 1 && children.length === 1) {
      return elementChildren[0] as HTMLElement;
    }

    const wrapper = this.renderer.createElement('div');
    this.renderer.setAttribute(wrapper, 'data-slide-wrapper', 'true');
    this.renderer.addClass(wrapper, 'app-slide-wrapper');

    children.forEach(child => this.renderer.appendChild(wrapper, child));

    this.renderer.appendChild(host, wrapper);
    return wrapper;
  }

  private setupHostStyles(): void {
    if (this.containerElement) {
      this.renderer.addClass(this.containerElement, 'app-slide-container');
    }

    if (this.textElement) {
      this.renderer.addClass(this.textElement, 'app-slide-text');
      if (this.ellipsis) {
        this.renderer.addClass(this.textElement, 'app-slide-ellipsis');
      }
    }

    if (this.slideTarget === 'self') {
      const host = this.el.nativeElement;
      this.renderer.addClass(host, 'app-slide-text');
    }
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined' || !this.containerElement) return;

    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleOverflowCheck();
      });
      this.resizeObserver.observe(this.containerElement!);
    });
  }

  private setupMutationObserver(): void {
    if (!this.textElement) return;

    this.ngZone.runOutsideAngular(() => {
      this.mutationObserver = new MutationObserver(() => {
        this.scheduleOverflowCheck();
      });
      this.mutationObserver.observe(this.textElement!, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    });
  }

  private scheduleOverflowCheck(): void {
    if (this.checkTimeoutId) clearTimeout(this.checkTimeoutId);
    this.checkTimeoutId = setTimeout(() => this.checkOverflow(), 50);
  }

  private checkOverflow(): void {
    if (!this.textElement || !this.containerElement) return;

    const containerRect = this.containerElement.getBoundingClientRect();
    const containerWidth = containerRect.width > 0 ? containerRect.width : this.containerElement.clientWidth;
    const textWidth = this.textElement.scrollWidth;

    if (containerWidth === 0) return;

    const wasOverflowing = this.isOverflowing;
    this.isOverflowing = textWidth > containerWidth;

    if (this.isOverflowing) {
      const overflow = textWidth - containerWidth + this.slideMargin;
      this.setupAnimationStyles(overflow);

      if (this.ellipsis) {
        this.renderer.removeClass(this.textElement, 'app-slide-ellipsis');
      }

      if (this.mode === 'loop' && !wasOverflowing) {
        this.startLoopAnimation();
      }
    } else {
      this.removeAnimationStyles();
      this.stopAnimation();

      if (this.ellipsis && this.textElement) {
        this.renderer.addClass(this.textElement, 'app-slide-ellipsis');
      }
    }
  }

  private setupAnimationStyles(overflow: number): void {
    if (!this.textElement) return;
    this.removeStyleElement();

    const slideDuration = overflow / this.speed;
    let keyframes: string;
    let duration: number;

    if (this.mode === 'loop' || this.mode === 'hover-loop') {
      const totalDuration = this.pauseStart + slideDuration + this.pauseEnd;
      const pauseStartPct = (this.pauseStart / totalDuration) * 100;
      const slidePct = ((this.pauseStart + slideDuration) / totalDuration) * 100;
      const pauseEndPct = ((this.pauseStart + slideDuration + this.pauseEnd * 0.99) / totalDuration) * 100;

      keyframes = `
        @keyframes textSlide-${this.UNIQUE_ID} {
          0% { transform: translateX(0); }
          ${pauseStartPct.toFixed(1)}% { transform: translateX(0); }
          ${slidePct.toFixed(1)}% { transform: translateX(-${overflow}px); }
          ${pauseEndPct.toFixed(1)}% { transform: translateX(-${overflow}px); }
          ${(pauseEndPct + 0.1).toFixed(1)}% { transform: translateX(0); }
          100% { transform: translateX(0); }
        }
      `;
      duration = totalDuration;
    } else {
      keyframes = `
        @keyframes textSlide-${this.UNIQUE_ID} {
          0% { transform: translateX(0); }
          100% { transform: translateX(-${overflow}px); }
        }
      `;
      duration = slideDuration;
    }

    this.styleElement = this.renderer.createElement('style');
    this.styleElement.textContent = keyframes;
    this.renderer.appendChild(document.head, this.styleElement);
    this.renderer.setAttribute(this.textElement, 'data-slide-duration', duration.toString());
  }

  private removeAnimationStyles(): void {
    if (this.textElement) {
      this.renderer.removeStyle(this.textElement, 'animation');
      this.renderer.removeAttribute(this.textElement, 'data-slide-duration');
    }
  }

  private removeStyleElement(): void {
    if (this.styleElement && this.styleElement.parentNode) {
      this.renderer.removeChild(document.head, this.styleElement);
      this.styleElement = null;
    }
  }

  private startHoverAnimation(): void {
    if (!this.textElement || this.isAnimating) return;

    const duration = this.textElement.getAttribute('data-slide-duration') || '1';
    this.isAnimating = true;

    this.renderer.setStyle(this.textElement, 'animation', `textSlide-${this.UNIQUE_ID} ${duration}s linear forwards`);

    const durationMs = parseFloat(duration) * 1000;
    if (this.animationTimeoutId) clearTimeout(this.animationTimeoutId);

    this.animationTimeoutId = setTimeout(() => {
      if (this.textElement) {
        this.renderer.setStyle(this.textElement, 'animation', 'none');
        void this.textElement.offsetWidth;
        this.renderer.removeStyle(this.textElement, 'animation');
      }
      this.isAnimating = false;
    }, durationMs + 100);
  }

  private startLoopAnimation(): void {
    if (!this.textElement) return;

    const duration = this.textElement.getAttribute('data-slide-duration') || '1';

    this.renderer.setStyle(this.textElement, 'animation', `textSlide-${this.UNIQUE_ID} ${duration}s linear infinite`);
    this.isAnimating = true;
  }

  private stopAnimation(): void {
    if (this.animationTimeoutId) {
      clearTimeout(this.animationTimeoutId);
      this.animationTimeoutId = null;
    }

    if (this.textElement) {
      this.renderer.setStyle(this.textElement, 'animation', 'none');
      void this.textElement.offsetWidth;
      this.renderer.removeStyle(this.textElement, 'animation');
    }
    this.isAnimating = false;
  }

  private cleanup(): void {
    if (this.checkTimeoutId) clearTimeout(this.checkTimeoutId);
    if (this.animationTimeoutId) clearTimeout(this.animationTimeoutId);

    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();

    this.intersectionObserver = null;
    this.resizeObserver = null;
    this.mutationObserver = null;

    this.removeStyleElement();
    this.stopAnimation();
  }
}
